import { useState, useEffect } from 'react';

import { getUsers, createUser, updateUser, deactivateUser } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../utils/toastConfig';
import { FiPlus, FiEdit2, FiUserX, FiRefreshCw } from 'react-icons/fi';
import './crmPages.css';

const ROLE_OPTIONS = ['sales', 'hr', 'ceo'];

const emptyForm = {
  username: '',
  email: '',
  full_name: '',
  phone: '',
  role: 'sales',
  password: '',
  is_active: true,
};

export default function UserManagement() {
  const { user } = useAuth();
  const isCEO = user?.role === 'ceo' || user?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data } = await getUsers();
      setUsers(data || []);
    } catch (err) {
      showErrorToast('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username,
      email: u.email,
      full_name: u.full_name || '',
      phone: u.phone || '',
      role: u.role,
      password: '',
      is_active: !!u.is_active,
    });
    setShowModal(true);
  };

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) { showErrorToast('Username is required'); return; }
    if (!form.email.trim())    { showErrorToast('Email is required'); return; }
    if (!editing && (!form.password || form.password.length < 6)) {
      showErrorToast('Password is required (min 6 chars)'); return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await updateUser(editing.id, payload);
        showSuccessToast('User updated');
      } else {
        await createUser(form);
        showSuccessToast('User created');
      }
      setShowModal(false);
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate user "${u.username}"? They will no longer be able to log in.`)) return;
    try {
      await deactivateUser(u.id);
      showSuccessToast('User deactivated');
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Failed');
    }
  };

  // Stats
  const active   = users.filter((u) => u.is_active).length;
  const salesCnt = users.filter((u) => u.role === 'sales' && u.is_active).length;
  const hrCnt    = users.filter((u) => u.role === 'hr'    && u.is_active).length;
  const ceoCnt   = users.filter((u) => u.role === 'ceo'   && u.is_active).length;

  return (
    <div className="cp-shell">

      <main className="cp-main">
        <div className="cp-page-header">
          <div>
            <h1>User Management</h1>
            <p>Onboard sales reps, assign roles, and manage account access.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cp-btn cp-btn--ghost" onClick={loadAll}><FiRefreshCw /> Refresh</button>
            <button className="cp-btn cp-btn--primary" onClick={openCreate}><FiPlus /> New User</button>
          </div>
        </div>

        <div className="cp-stats">
          <div className="cp-stat"><div className="cp-stat__label">Active Users</div><div className="cp-stat__value">{active}</div></div>
          <div className="cp-stat"><div className="cp-stat__label">Sales Reps</div><div className="cp-stat__value">{salesCnt}</div></div>
          <div className="cp-stat"><div className="cp-stat__label">HR</div><div className="cp-stat__value">{hrCnt}</div></div>
          <div className="cp-stat"><div className="cp-stat__label">CEO</div><div className="cp-stat__value">{ceoCnt}</div></div>
        </div>

        {!isCEO && (
          <div className="cp-banner">
            You are logged in as HR — you can manage Sales and HR users, but cannot modify CEO accounts.
          </div>
        )}

        {loading ? (
          <div className="cp-empty">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="cp-card cp-empty">
            <div className="cp-empty__title">No users yet</div>
          </div>
        ) : (
          <div className="cp-table-wrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === user?.id;
                  const hrBlocked = !isCEO && u.role === 'ceo';
                  return (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.username}{isSelf && ' (you)'}</td>
                      <td>{u.full_name || '—'}</td>
                      <td>{u.email}</td>
                      <td>{u.phone || '—'}</td>
                      <td><span className={`top-nav__role top-nav__role--${u.role}`}>{u.role?.toUpperCase()}</span></td>
                      <td>
                        {u.is_active
                          ? <span className="cp-badge cp-badge--active">Active</span>
                          : <span className="cp-badge cp-badge--expired">Inactive</span>}
                      </td>
                      <td>
                        <button
                          className="cp-btn cp-btn--ghost cp-btn--sm"
                          onClick={() => openEdit(u)}
                          disabled={hrBlocked}
                          title={hrBlocked ? 'Only CEO can edit CEO accounts' : 'Edit'}
                        >
                          <FiEdit2 />
                        </button>
                        {u.is_active && !isSelf && !hrBlocked && (
                          <button
                            className="cp-btn cp-btn--danger cp-btn--sm"
                            onClick={() => handleDeactivate(u)}
                            title="Deactivate"
                            style={{ marginLeft: 4 }}
                          >
                            <FiUserX />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="cp-modal-backdrop" onClick={() => !saving && setShowModal(false)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cp-modal__header">
                <h2>{editing ? `Edit ${editing.username}` : 'New User'}</h2>
                <button className="cp-modal__close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form className="cp-form" onSubmit={handleSubmit}>
                <div className="cp-field">
                  <label>Username *</label>
                  <input value={form.username} onChange={(e) => handleChange('username', e.target.value)} disabled={!!editing} required />
                </div>
                <div className="cp-field">
                  <label>Email *</label>
                  <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} required />
                </div>
                <div className="cp-field">
                  <label>Full Name</label>
                  <input value={form.full_name} onChange={(e) => handleChange('full_name', e.target.value)} />
                </div>
                <div className="cp-field">
                  <label>Phone</label>
                  <input value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                </div>
                <div className="cp-field">
                  <label>Role *</label>
                  <select value={form.role} onChange={(e) => handleChange('role', e.target.value)}>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r} disabled={!isCEO && (r === 'ceo' || r === 'hr')}>
                        {r.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cp-field">
                  <label>{editing ? 'Reset Password (optional)' : 'Password *'}</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder={editing ? 'Leave blank to keep current' : 'Min 6 characters'}
                    autoComplete="new-password"
                  />
                </div>
                {editing && (
                  <div className="cp-field cp-field--full">
                    <label>Account Status</label>
                    <select value={form.is_active ? '1' : '0'} onChange={(e) => handleChange('is_active', e.target.value === '1')}>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                )}
                <div className="cp-form-actions">
                  <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="cp-btn cp-btn--primary" disabled={saving}>
                    {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create User')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
